import type { Episode, ProjectConfig, Season, Sequence } from "../../models/production";
import type { Shot } from "../../types";
import { deriveHierarchyFromShotCode, validateShotCode } from "../../shared/shotCodeParser";
import { mapLegacyBidStatusToSeparatedStatuses } from "../../shared/statusConfig";

export interface ShotHierarchyBackfillUpdate {
  shotId: string;
  patch: Partial<Shot>;
}

export interface ShotHierarchyBackfillResult {
  updates: ShotHierarchyBackfillUpdate[];
  invalidShots: Array<{ shotId: string; shotCode: string; errors: string[] }>;
}

export interface MissingHierarchyEntitiesResult {
  seasons: Season[];
  episodes: Episode[];
  sequences: Sequence[];
}

function buildId(...parts: Array<string | number | undefined>) {
  return parts.filter(Boolean).join(":");
}

export function backfillShotHierarchyFromShotCodes(
  projectId: string,
  shots: Shot[],
  projectConfig: Pick<ProjectConfig, "shotCodeParserConfig">
): ShotHierarchyBackfillResult {
  const updates: ShotHierarchyBackfillUpdate[] = [];
  const invalidShots: Array<{ shotId: string; shotCode: string; errors: string[] }> = [];

  for (const shot of shots) {
    const validation = validateShotCode(shot.shotCode, projectConfig.shotCodeParserConfig);
    if (!validation.isValid || !validation.parsed) {
      invalidShots.push({ shotId: shot.id, shotCode: shot.shotCode, errors: validation.errors });
      continue;
    }

    const hierarchy = deriveHierarchyFromShotCode(shot.shotCode, projectConfig.shotCodeParserConfig);
    const separatedStatuses = mapLegacyBidStatusToSeparatedStatuses(shot.status);

    updates.push({
      shotId: shot.id,
      patch: {
        projectId,
        parsedShotCode: validation.parsed,
        seasonId: hierarchy.seasonNumber !== undefined ? buildId(projectId, "season", hierarchy.seasonNumber) : undefined,
        episodeId: hierarchy.seasonEpisodeCode ? buildId(projectId, "episode", hierarchy.seasonEpisodeCode) : undefined,
        sequenceId: hierarchy.sequenceCode ? buildId(projectId, "sequence", hierarchy.sequenceCode) : undefined,
        awardStatus: shot.awardStatus || separatedStatuses.awardStatus,
        progressStatus: shot.progressStatus || separatedStatuses.progressStatus,
      },
    });
  }

  return { updates, invalidShots };
}

export function createMissingSequencesEpisodes(
  projectId: string,
  shots: Shot[],
  projectConfig: Pick<ProjectConfig, "shotCodeParserConfig">
): MissingHierarchyEntitiesResult {
  const seasons = new Map<string, Season>();
  const episodes = new Map<string, Episode>();
  const sequences = new Map<string, Sequence>();

  for (const shot of shots) {
    const validation = validateShotCode(shot.shotCode, projectConfig.shotCodeParserConfig);
    if (!validation.isValid) continue;

    const hierarchy = deriveHierarchyFromShotCode(shot.shotCode, projectConfig.shotCodeParserConfig);
    const now = new Date().toISOString();

    if (hierarchy.seasonNumber !== undefined) {
      const seasonId = buildId(projectId, "season", hierarchy.seasonNumber);
      if (!seasons.has(seasonId)) {
        seasons.set(seasonId, {
          id: seasonId,
          projectId,
          seasonNumber: hierarchy.seasonNumber,
          title: `Season ${hierarchy.seasonNumber}`,
          code: `S${String(hierarchy.seasonNumber).padStart(2, "0")}`,
          createdAt: now,
          updatedAt: now,
        });
      }
    }

    if (hierarchy.seasonEpisodeCode && hierarchy.episodeNumber !== undefined) {
      const seasonId = hierarchy.seasonNumber !== undefined ? buildId(projectId, "season", hierarchy.seasonNumber) : "";
      const episodeId = buildId(projectId, "episode", hierarchy.seasonEpisodeCode);
      if (!episodes.has(episodeId)) {
        episodes.set(episodeId, {
          id: episodeId,
          projectId,
          seasonId,
          episodeNumber: hierarchy.episodeNumber,
          seasonEpisodeCode: hierarchy.seasonEpisodeCode,
          code: hierarchy.episodeCode,
          title: `Episode ${String(hierarchy.episodeNumber).padStart(2, "0")}`,
          createdAt: now,
          updatedAt: now,
        });
      }
    }

    if (hierarchy.sequenceCode && hierarchy.sequenceNumber !== undefined && hierarchy.seasonEpisodeCode) {
      const sequenceId = buildId(projectId, "sequence", hierarchy.sequenceCode);
      if (!sequences.has(sequenceId)) {
        sequences.set(sequenceId, {
          id: sequenceId,
          projectId,
          seasonId: hierarchy.seasonNumber !== undefined ? buildId(projectId, "season", hierarchy.seasonNumber) : undefined,
          episodeId: buildId(projectId, "episode", hierarchy.seasonEpisodeCode),
          sequenceNumber: hierarchy.sequenceNumber,
          code: hierarchy.sequenceCode,
          title: `Sequence ${String(hierarchy.sequenceNumber).padStart(3, "0")}`,
          createdAt: now,
          updatedAt: now,
        });
      }
    }
  }

  return {
    seasons: Array.from(seasons.values()),
    episodes: Array.from(episodes.values()),
    sequences: Array.from(sequences.values()),
  };
}
